import React, { useEffect, useMemo, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableActions from "./table-actions";
import { NoData } from "src/shared/components/tables/no-data";
import { TableLoadingSkeleton } from "src/shared/components/tables/table-loading-skeleton";
import {
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TablePagination,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import AddBoxIcon from "@mui/icons-material/AddBox";
import { useNavigate } from "react-router-dom";
import StatusChip from "src/shared/components/status-chip";
import { useRecoilState } from "recoil";
import { authState } from "src/store/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "src/firebase";
import { ICity, IEnumData } from "src/types/generic";
import { locationServices, serviceLabels } from "src/constants/general";
import { useGetCitiesList } from "src/api/general";
import CustomAutocomplete from "src/shared/components/custom-auto-complete";
import { useGetClientsList } from "src/api/clients";

const tableHeadTextLabels = [
  "Name in English",
  "Name in Arabic",
  "NO. of Locations",
  "Agreement Type",
  "Valet",
  "Self-Parking",
  "Actions",
];

const ActiveClientsTable = ({}: {}) => {
  const navigate = useNavigate();
  const [auth] = useRecoilState(authState);
  const { permissions } = auth;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tempSearchBy, setTempSearchBy] = useState<string>("");
  const [searchBy, setSearchBy] = useState<string>("");
  const [tempSelectedService, setTempSelectedService] = useState<string>("all");
  const [selectedService, setSelectedService] = useState<any>("all");
  const [agreementTypes, setAgreementTypes] = useState<IEnumData[]>([]);
  const [tempSelectedAgreement, setTempSelectedAgreement] =
    useState<any>("all");
  const [selectedAgreement, setSelectedAgreement] = useState<any>("all");
  const [selectedCity, setSelectedCity] = useState<ICity>();
  const [tempSelectedCity, setTempSelectedCity] = useState<ICity>();

  // apis
  const { data: citiesData } = useGetCitiesList({ type: "active" });
  const { data, isLoading, refetch } = useGetClientsList({
    type: "active",
    cityId: selectedCity?.id,
  });

  const rowsList = data?.data || [];

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (searchedVal: string) => {
    setPage(0);
    setTempSearchBy(searchedVal);
  };

  const clearSearch = () => {
    setTempSearchBy("");
    if (searchBy !== "") {
      setSearchBy("");
      setPage(0);
    }
  };

  const handleChangeServiceType = (e: SelectChangeEvent) => {
    const value = e.target.value;
    setTempSelectedService(value);
  };

  const handleChangeAgreementType = (e: SelectChangeEvent) => {
    const value = e.target.value;
    setTempSelectedAgreement(value);
  };

  const handleChangeCity = (event: React.SyntheticEvent, newValue: any) => {
    setTempSelectedCity(newValue);
  };

  const handleApply = () => {
    setSelectedService(tempSelectedService);
    setSearchBy(tempSearchBy);
    setSelectedAgreement(tempSelectedAgreement);
    setSelectedCity(tempSelectedCity);
  };

  const handleResetFilers = () => {
    setPage(0);
    setSelectedService("all");
    setSearchBy("");
    setSelectedAgreement("all");
    setSelectedCity(undefined);

    setTempSelectedService("all");
    setTempSearchBy("");
    setTempSelectedAgreement("all");
    setTempSelectedCity(undefined);
  };

  const handleSearchAndFilters = () => {
    let searchAndFiltersRows = [...rowsList];

    //Search by Arabic or English name
    if (searchBy.trim()) {
      const filteredRows = searchAndFiltersRows.filter((row) => {
        return (
          row.clientInfo?.nameEn
            ?.toLowerCase()
            .includes(searchBy.toLowerCase()) ||
          row.clientInfo?.nameAr?.toLowerCase().includes(searchBy.toLowerCase())
        );
      });
      searchAndFiltersRows = filteredRows;
    }

    // Filter by service
    if (selectedService === "valet") {
      const filteredRows = searchAndFiltersRows.filter((row) => {
        return row.clientInfo.hasValetService;
      });
      searchAndFiltersRows = filteredRows;
    } else if (selectedService === "selfParking") {
      const filteredRows = searchAndFiltersRows.filter((row) => {
        return row.clientInfo.hasSelfParkingService;
      });
      searchAndFiltersRows = filteredRows;
    } else if (selectedService === "golfCar") {
      const filteredRows = searchAndFiltersRows.filter((row: any) => {
        return row.clientInfo.hasGolfCarService;
      });
      searchAndFiltersRows = filteredRows;
    } else if (selectedService === "partialCarWash") {
      const filteredRows = searchAndFiltersRows.filter((row: any) => {
        return row.clientInfo.hasPartialCarWashService;
      });
      searchAndFiltersRows = filteredRows;
    } else if (selectedService === "fullCarWash") {
      const filteredRows = searchAndFiltersRows.filter((row: any) => {
        return row.clientInfo.hasFullCarWashService;
      });
      searchAndFiltersRows = filteredRows;
    }

    //Filter by agreement
    if (selectedAgreement !== "all") {
      const filteredRows = searchAndFiltersRows.filter((row) => {
        return row.clientInfo?.agreementType === selectedAgreement;
      });
      searchAndFiltersRows = filteredRows;
    }

    return searchAndFiltersRows;
  };

  const searchAndFiltersLocations = handleSearchAndFilters();

  const paginatedVisibleRows = useMemo(
    () =>
      searchAndFiltersLocations.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      ),
    [page, rowsPerPage, rowsList, searchBy, selectedService, selectedAgreement], // filters changes rerender
  );

  const emptyDataArr = searchAndFiltersLocations.length === 0 && !isLoading;

  const handleNavigate = () => {
    navigate("/clients/active/add");
  };

  useEffect(() => {
    setPage(0);
  }, [selectedService, selectedAgreement]);

  // Fetch agreement types enum
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    const fetchData = async () => {
      try {
        const docRef = doc(db, "enums", "clientAgreementTypes");

        unsubscribe = onSnapshot(docRef, (docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            setAgreementTypes(data.data);
          } else {
            console.warn("No such enum document found!");
          }
        });
      } catch (error) {
        console.error("Error fetching enum:", error);
      }
    };

    fetchData();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  return (
    <>
      {permissions?.clients.create ? (
        <Stack mb={2.2}>
          <Button
            variant="contained"
            sx={{ gap: 0.5, ml: { xs: "auto", lg: "auto" } }}
            onClick={handleNavigate}
          >
            <AddBoxIcon fontSize="small" sx={{ opacity: 0.7 }} />
            Add New Client
          </Button>
        </Stack>
      ) : null}
      <Stack direction={{ xs: "column", md: "row" }} gap={"10px"} mb={3}>
        <Stack direction={"row"} gap={"10px"} flexWrap={"wrap"} flexGrow={1}>
          <TextField
            size="small"
            placeholder="Search"
            sx={{ width: { xs: "100%", sm: "250px" } }}
            value={tempSearchBy}
            onChange={(event) => handleSearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment
                  position="end"
                  sx={{ opacity: !tempSearchBy ? 0 : 1 }}
                >
                  <IconButton size="small" onClick={clearSearch}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* city */}
          <CustomAutocomplete
            key={tempSelectedCity?.id ?? "city-autocomplete"}
            sx={{ width: { xs: "100%", sm: "170px" } }}
            size="small"
            disablePortal
            id="combo-box-demo"
            options={citiesData?.data ?? []}
            getOptionLabel={(option) => option.nameEn}
            getOptionKey={(option) => `${option.id}`}
            value={tempSelectedCity}
            onChange={handleChangeCity}
            renderInput={(params: any) => (
              <TextField fullWidth {...params} label={"City"} />
            )}
          />

          <FormControl size="small" sx={{ minWidth: "180px" }}>
            <InputLabel id="demo-simple-select-label">Service</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={tempSelectedService}
              label="Service"
              onChange={handleChangeServiceType}
            >
              <MenuItem value={"all"}>All</MenuItem>
              {locationServices.map((service: any) => (
                <MenuItem key={service} value={service}>
                  {serviceLabels[service] ?? service}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: "180px" }}>
            <InputLabel id="demo-simple-select-label">
              Agreement Type
            </InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={tempSelectedAgreement}
              label="Agreement Type"
              onChange={handleChangeAgreementType}
            >
              <MenuItem value={"all"}>All</MenuItem>
              {agreementTypes.map((agreement) => (
                <MenuItem key={agreement.key} value={agreement.key}>
                  {agreement.label.en}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <Stack direction={"row"}>
          <Button onClick={handleResetFilers}>Clear</Button>
          <Button
            variant="contained"
            onClick={handleApply}
            sx={{ width: "100px" }}
          >
            Apply
          </Button>
        </Stack>
      </Stack>
      <TableContainer
        sx={{
          bgcolor: "#1A1A1A",
          boxShadow: "0px 14px 44px 0px rgba(0, 0, 0, 0.35)",
          padding: "10px 30px",
        }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              {tableHeadTextLabels.map((item) => (
                <TableCell
                  key={item}
                  sx={{
                    fontWeight: 600,
                    color: "#A1A5B7",
                  }}
                  align={item === "Actions" ? "center" : "left"}
                >
                  {item}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {isLoading ? (
            <TableLoadingSkeleton rowsLength={8} cellsLength={7} />
          ) : (
            <TableBody>
              {paginatedVisibleRows.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{
                    "&:last-child td, &:last-child th": {
                      border: 0,
                    },
                  }}
                >
                  <TableCell>{row.clientInfo?.nameEn}</TableCell>
                  <TableCell>{row.clientInfo?.nameAr}</TableCell>
                  <TableCell>{row.clientInfo.numberOfLocations}</TableCell>
                  <TableCell>{row.clientInfo?.agreementTypeLabel}</TableCell>
                  <TableCell>
                    <StatusChip
                      color={
                        row.clientInfo.hasValetService ? "success" : "warning"
                      }
                      label={
                        row.clientInfo.hasValetService ? "ACTIVE" : "INACTIVE"
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <StatusChip
                      label={
                        row.clientInfo.hasSelfParkingService
                          ? "ACTIVE"
                          : "INACTIVE"
                      }
                      color={
                        row.clientInfo.hasSelfParkingService
                          ? "success"
                          : "warning"
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Stack alignItems={"center"}>
                      <TableActions
                        rowId={row.id as string}
                        refetch={refetch}
                      />
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>

        {emptyDataArr && <NoData />}

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={searchAndFiltersLocations.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </>
  );
};
export default ActiveClientsTable;
